/**
 * Ignus Arcana Field Mapping
 *
 * Field mapping for the Ignus Arcana character sheet template.
 * Maps character data to the form fields created by add-pdf-form-fields.mjs
 */

import type { CharacterSheetData, IgnusCharacterSheetData, PdfFieldMapping } from '../types';
import { formatModifier } from '../../../utils/formatting';

/**
 * Check if data has Ignus-specific fields
 */
function isIgnusData(data: CharacterSheetData | IgnusCharacterSheetData): data is IgnusCharacterSheetData {
  return 'species' in data;
}

export function mapIgnusArcanaFields(data: CharacterSheetData | IgnusCharacterSheetData): PdfFieldMapping[] {
  const mappings: PdfFieldMapping[] = [];

  // === PAGE 1: Main Stats ===

  // Header
  mappings.push({ fieldName: 'CharacterName', value: data.characterName, fieldType: 'text' });
  mappings.push({ fieldName: 'Background', value: data.background, fieldType: 'text' });
  mappings.push({ fieldName: 'Level', value: data.classLevel.split(' ').pop() || '1', fieldType: 'text' });

  // Species/Race handling
  if (isIgnusData(data)) {
    mappings.push({ fieldName: 'Species', value: data.species, fieldType: 'text' });
    mappings.push({ fieldName: 'Lineage', value: data.lineage || '', fieldType: 'text' });
    mappings.push({ fieldName: 'Faction', value: data.factionName || data.faction || '', fieldType: 'text' });
    mappings.push({ fieldName: 'CreatureType', value: data.creatureType, fieldType: 'text' });
    mappings.push({ fieldName: 'RestMechanic', value: data.restMechanic, fieldType: 'text' });
    mappings.push({ fieldName: 'DamageResistances', value: data.damageResistances.join(', '), fieldType: 'text' });
  } else {
    mappings.push({ fieldName: 'Species', value: data.race, fieldType: 'text' });
  }

  // Ability Scores
  const abilities = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'] as const;
  const abilityAbbr = ['STR', 'DEX', 'CON', 'INT', 'WIS', 'CHA'];

  abilities.forEach((ability, index) => {
    const abbr = abilityAbbr[index];
    const abilityData = data.abilities[ability];

    mappings.push({
      fieldName: abbr,
      value: String(abilityData.score),
      fieldType: 'text',
    });
    mappings.push({
      fieldName: `${abbr}mod`,
      value: formatModifier(abilityData.modifier),
      fieldType: 'text',
    });

    // Saving throws
    const saveBonus = data.savingThrowModifiers[ability];
    mappings.push({
      fieldName: `${abbr}_Save`,
      value: formatModifier(saveBonus),
      fieldType: 'text',
    });
  });

  // Combat Stats
  mappings.push({ fieldName: 'ProfBonus', value: formatModifier(data.proficiencyBonus), fieldType: 'text' });
  mappings.push({ fieldName: 'AC', value: String(data.armorClass), fieldType: 'text' });
  mappings.push({ fieldName: 'Initiative', value: formatModifier(data.initiative), fieldType: 'text' });
  mappings.push({ fieldName: 'Speed', value: String(data.speed), fieldType: 'text' });
  mappings.push({ fieldName: 'HP', value: String(data.hitPointMaximum), fieldType: 'text' });
  mappings.push({ fieldName: 'HitDice', value: data.hitDice, fieldType: 'text' });

  // Skills
  const skillFieldMap: Record<string, string> = {
    'acrobatics': 'Acrobatics',
    'animal-handling': 'AnimalHandling',
    'arcana': 'Arcana',
    'athletics': 'Athletics',
    'deception': 'Deception',
    'history': 'History',
    'insight': 'Insight',
    'intimidation': 'Intimidation',
    'investigation': 'Investigation',
    'medicine': 'Medicine',
    'nature': 'Nature',
    'perception': 'Perception',
    'performance': 'Performance',
    'persuasion': 'Persuasion',
    'religion': 'Religion',
    'sleight-of-hand': 'SleightOfHand',
    'stealth': 'Stealth',
    'survival': 'Survival',
  };

  for (const [skillId, fieldName] of Object.entries(skillFieldMap)) {
    const modifier = data.skillModifiers[skillId as keyof typeof data.skillModifiers];
    if (modifier !== undefined) {
      mappings.push({
        fieldName,
        value: formatModifier(modifier),
        fieldType: 'text',
      });
    }
  }

  // Attacks (up to 12 weapons)
  for (let i = 0; i < Math.min(data.attacks.length, 12); i++) {
    const attack = data.attacks[i];
    const num = i + 1;
    mappings.push({ fieldName: `Weapon${num}Name`, value: attack.name, fieldType: 'text' });
    mappings.push({ fieldName: `Weapon${num}AtkBonus`, value: attack.bonus, fieldType: 'text' });
    mappings.push({ fieldName: `Weapon${num}Damage`, value: `${attack.damage} ${attack.damageType || ''}`.trim(), fieldType: 'text' });
  }

  // Features and Traits
  mappings.push({ fieldName: 'FeaturesTraits', value: data.featuresAndTraits, fieldType: 'text' });

  // === PAGE 2: Backstory & Details ===

  mappings.push({ fieldName: 'PlayerName', value: data.playerName, fieldType: 'text' });
  mappings.push({ fieldName: 'Experience', value: data.experiencePoints, fieldType: 'text' });

  // Appearance
  mappings.push({ fieldName: 'Age', value: data.appearance.age, fieldType: 'text' });
  mappings.push({ fieldName: 'Height', value: data.appearance.height, fieldType: 'text' });
  mappings.push({ fieldName: 'Weight', value: data.appearance.weight, fieldType: 'text' });
  mappings.push({ fieldName: 'Eyes', value: data.appearance.eyes, fieldType: 'text' });
  mappings.push({ fieldName: 'Skin', value: data.appearance.skin, fieldType: 'text' });
  mappings.push({ fieldName: 'Hair', value: data.appearance.hair, fieldType: 'text' });

  // Personality
  mappings.push({ fieldName: 'PersonalityTraits', value: data.personalityTraits, fieldType: 'text' });
  mappings.push({ fieldName: 'Ideals', value: data.ideals, fieldType: 'text' });
  mappings.push({ fieldName: 'Bonds', value: data.bonds, fieldType: 'text' });
  mappings.push({ fieldName: 'Flaws', value: data.flaws, fieldType: 'text' });
  mappings.push({ fieldName: 'Backstory', value: data.backstory, fieldType: 'text' });

  // === PAGE 3: Spellcasting ===

  if (data.spellcasting) {
    mappings.push({ fieldName: 'SpellcastingClass', value: data.spellcasting.spellcastingClass, fieldType: 'text' });
    mappings.push({ fieldName: 'SpellcastingAbility', value: data.spellcasting.spellcastingAbility, fieldType: 'text' });
    mappings.push({ fieldName: 'SpellSaveDC', value: String(data.spellcasting.spellSaveDC), fieldType: 'text' });
    mappings.push({ fieldName: 'SpellAttackBonus', value: formatModifier(data.spellcasting.spellAttackBonus), fieldType: 'text' });

    // Spell slots
    if (data.spellcasting.spellSlots) {
      const slotLevels = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th'];
      for (let i = 0; i < 9; i++) {
        const slotData = data.spellcasting.spellSlots[i];
        if (slotData && slotData.total > 0) {
          mappings.push({
            fieldName: `Slots${slotLevels[i]}`,
            value: String(slotData.total),
            fieldType: 'text',
          });
        }
      }
    }

    // Spells by level
    if (data.spellcasting.spellsByLevel) {
      // Cantrips
      if (data.spellcasting.spellsByLevel[0]?.length > 0) {
        mappings.push({
          fieldName: 'Cantrips',
          value: data.spellcasting.spellsByLevel[0].join('\n'),
          fieldType: 'text',
        });
      }

      // Leveled spells
      const spellFieldLevels = ['1st', '2nd', '3rd', '4th', '5th'];
      for (let level = 1; level <= 5; level++) {
        const spells = data.spellcasting.spellsByLevel[level];
        if (spells?.length > 0) {
          mappings.push({
            fieldName: `Spells${spellFieldLevels[level - 1]}`,
            value: spells.join('\n'),
            fieldType: 'text',
          });
        }
      }
    }
  }

  return mappings;
}
